import { useState, useMemo, useEffect } from 'react';
import { AnswersState, Question } from '../types';
import { questions } from '../config/questions';
import { quickModeQuestions } from '../config/quickModeQuestions';
import { getQuestionsWithAnswers } from '../utils/questionFiltering';

export const usePromptFlow = (initialState: {
  initialAnswers: AnswersState,
  initialQuestionId: string,
  initialUserIdea: string
}) => {
  const [currentQuestionId, setCurrentQuestionId] = useState<string>(initialState.initialQuestionId);
  const [answers, setAnswers] = useState<AnswersState>(initialState.initialAnswers);
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);
  const [userIdea, setUserIdea] = useState(initialState.initialUserIdea);

  const relevantQuestions = useMemo((): Question[] => {
    if (isAdvancedMode) {
      // Use full question set and advanced filtering
      return getQuestionsWithAnswers(answers, false);
    }
    // Use simplified quick mode questions
    return quickModeQuestions;
  }, [answers, isAdvancedMode]);

  const currentQuestionIndex = relevantQuestions.findIndex(q => q.id === currentQuestionId);
  const currentQuestion = relevantQuestions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === relevantQuestions.length - 1;
  const isFirstQuestion = currentQuestionIndex === 0;

  // Recalculate currentQuestionId on state change if invalid
  useEffect(() => {
    if (currentQuestionIndex === -1 && relevantQuestions.length > 0) {
      setCurrentQuestionId(relevantQuestions[0].id);
    }
  }, [relevantQuestions, currentQuestionIndex]);

  const canProceed = useMemo(() => {
    if (!currentQuestion) return false;

    // Simplified validation: if required, ensure an answer exists/is non-empty
    return !currentQuestion.required || 
           currentQuestion.type === 'upload-prompt' ||
           (currentQuestion.type === 'single' && answers[currentQuestion.id] !== undefined) ||
           (currentQuestion.type === 'multiple' && Array.isArray(answers[currentQuestion.id]) && (answers[currentQuestion.id] as string[]).length > 0) ||
           (currentQuestion.type === 'text' && typeof answers[currentQuestion.id] === 'string' && (answers[currentQuestion.id] as string).trim().length > 0);
  }, [currentQuestion, answers]);

  const handleAnswer = (answer: string | string[]) => {
    setAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: answer
    }));
    
    // Auto-advance logic for single-selects
    if (!isLastQuestion && currentQuestion.type === 'single') {
      setTimeout(() => {
        const nextIndex = currentQuestionIndex + 1;
        if (nextIndex < relevantQuestions.length) {
          setCurrentQuestionId(relevantQuestions[nextIndex].id);
        }
      }, 300);
    }
  };

  const handleMultipleSelect = (questionId: string, optionId: string) => {
    setAnswers(prev => {
      const currentValues = (prev[questionId] as string[]) || [];
      const isSelected = currentValues.includes(optionId);
      
      return {
        ...prev,
        [questionId]: isSelected 
          ? currentValues.filter(id => id !== optionId)
          : [...currentValues, optionId]
      };
    });
  };

  const handleNext = () => {
    if (isLastQuestion) return;
    const nextIndex = currentQuestionIndex + 1;
    if (nextIndex < relevantQuestions.length) {
      setCurrentQuestionId(relevantQuestions[nextIndex].id);
    }
  };

  const handlePrevious = () => {
    if (isFirstQuestion) return;
    const prevIndex = currentQuestionIndex - 1;
    if (prevIndex >= 0) {
      setCurrentQuestionId(relevantQuestions[prevIndex].id);
    }
  };
  
  const handleModeToggle = () => {
    const newMode = !isAdvancedMode;
    setIsAdvancedMode(newMode);
    
    // Reset to first question on mode switch
    setCurrentQuestionId('outputType');
    
    // Clear advanced-specific answers on switch to Quick Mode
    if (!newMode) {
      const basicAnswers: AnswersState = {};
      if (answers.outputType) basicAnswers.outputType = answers.outputType;
      if (answers.contentStyle) basicAnswers.contentStyle = answers.contentStyle;
      if (answers.targetUse) basicAnswers.targetUse = answers.targetUse;
      setAnswers(basicAnswers);
    }
  };

  const attributesString = useMemo(() => {
    // Generates the concatenated string of user choices for the LLM
    const entries = Object.entries(answers).filter(([_, value]) => value !== undefined && value !== '');
    
    const attributesArray = entries.map(([key, value]) => {
      if (Array.isArray(value)) return value.join(',');
      return value as string;
    });

    return attributesArray.join('-') + (isAdvancedMode ? '-advanced' : '-quick_mode');
  }, [answers, isAdvancedMode]);

  return {
    // State & Setters
    currentQuestionId, answers, isAdvancedMode, userIdea, setUserIdea, setAnswers,
    
    // Computed
    relevantQuestions, currentQuestionIndex, currentQuestion, isLastQuestion, isFirstQuestion, canProceed, attributesString,
    
    // Handlers
    handleAnswer, handleMultipleSelect, handleNext, handlePrevious, handleModeToggle,
  };
};